import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Save, Trash2, Loader2 } from "lucide-react";
import { UploadWidget } from "./UploadWidget";

/**
 * Generic entity form modal driven by a schema.
 * schema: [ { key, label, type: 'text'|'textarea'|'number'|'boolean'|'array'|'faqs'|'image'|'video'|'url', help, placeholder, rows, accept } ]
 */
export function EntityForm({ open, onClose, title, schema, initialValues = {}, onSubmit, submitting = false, error = "" }) {
    const [values, setValues] = useState(initialValues);
    useEffect(() => { setValues(initialValues || {}); }, [initialValues]);

    const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="entity-form-overlay">
            <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-border my-6" data-testid="entity-form-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white rounded-t-2xl z-10">
                    <h2 className="font-heading text-xl font-bold">{title}</h2>
                    <button onClick={onClose} data-testid="entity-form-close" className="h-8 w-8 rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); onSubmit?.(values); }}
                    className="p-6 space-y-5"
                    data-testid="entity-form"
                >
                    {schema.map((field) => (
                        <FieldRenderer key={field.key} field={field}
                            value={field.type === "section" ? undefined : values[field.key]}
                            onChange={(v) => field.type !== "section" && set(field.key, v)} />
                    ))}

                    {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                        <button type="button" onClick={onClose} data-testid="entity-form-cancel" className="rounded-md border border-input px-4 py-2 text-sm font-semibold hover:bg-secondary">Cancel</button>
                        <button type="submit" disabled={submitting} data-testid="entity-form-submit" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {submitting ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function FieldRenderer({ field, value, onChange }) {
    const inputCls = "w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
    const labelCls = "text-xs font-semibold uppercase tracking-widest text-muted-foreground";

    if (field.type === "textarea") {
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <textarea rows={field.rows || 4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={`mt-1.5 ${inputCls}`} data-testid={`entity-field-${field.key}`} />
                {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
            </div>
        );
    }

    if (field.type === "number") {
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} placeholder={field.placeholder} className={`mt-1.5 ${inputCls}`} data-testid={`entity-field-${field.key}`} />
            </div>
        );
    }

    if (field.type === "boolean") {
        return (
            <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-primary" data-testid={`entity-field-${field.key}`} />
                <span className="text-sm font-medium">{field.label}</span>
                {field.help && <span className="text-xs text-muted-foreground">— {field.help}</span>}
            </label>
        );
    }

    if (field.type === "array") {
        const arr = Array.isArray(value) ? value : [];
        const setAt = (i, v) => { const next = arr.slice(); next[i] = v; onChange(next); };
        const add = () => onChange([...arr, ""]);
        const del = (i) => onChange(arr.filter((_, idx) => idx !== i));
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <div className="mt-1.5 space-y-2" data-testid={`entity-field-${field.key}`}>
                    {arr.map((v, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input value={v} onChange={(e) => setAt(i, e.target.value)} placeholder={field.itemPlaceholder} className={inputCls} data-testid={`entity-field-${field.key}-${i}`} />
                            <button type="button" onClick={() => del(i)} className="h-9 w-9 rounded-md border border-input inline-flex items-center justify-center text-muted-foreground hover:text-red-600" data-testid={`entity-field-${field.key}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                    ))}
                    <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-input px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary" data-testid={`entity-field-${field.key}-add`}>
                        <Plus className="h-3.5 w-3.5" /> Add item
                    </button>
                </div>
            </div>
        );
    }

    if (field.type === "faqs") {
        const arr = Array.isArray(value) ? value : [];
        const setAt = (i, k, v) => { const next = arr.slice(); next[i] = { ...next[i], [k]: v }; onChange(next); };
        const add = () => onChange([...arr, { q: "", a: "" }]);
        const del = (i) => onChange(arr.filter((_, idx) => idx !== i));
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <div className="mt-1.5 space-y-3" data-testid={`entity-field-${field.key}`}>
                    {arr.map((f, i) => (
                        <div key={i} className="rounded-md border border-border p-3 bg-secondary/30 relative">
                            <input value={f.q || ""} onChange={(e) => setAt(i, "q", e.target.value)} placeholder="Question" className={inputCls} data-testid={`entity-faq-q-${i}`} />
                            <textarea rows={2} value={f.a || ""} onChange={(e) => setAt(i, "a", e.target.value)} placeholder="Answer" className={`mt-2 ${inputCls}`} data-testid={`entity-faq-a-${i}`} />
                            <button type="button" onClick={() => del(i)} className="absolute top-2 right-2 h-7 w-7 rounded-md text-muted-foreground hover:text-red-600 inline-flex items-center justify-center" data-testid={`entity-faq-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                    ))}
                    <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-input px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary" data-testid={`entity-field-${field.key}-add`}>
                        <Plus className="h-3.5 w-3.5" /> Add FAQ
                    </button>
                </div>
            </div>
        );
    }

    if (field.type === "object-array") {
        const arr = Array.isArray(value) ? value : [];
        const setAt = (i, k, v) => { const next = arr.slice(); next[i] = { ...next[i], [k]: v }; onChange(next); };
        const add = () => onChange([...arr, {}]);
        const del = (i) => onChange(arr.filter((_, idx) => idx !== i));
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                {field.help && <p className="mt-0.5 text-xs text-muted-foreground">{field.help}</p>}
                <div className="mt-1.5 space-y-3" data-testid={`entity-field-${field.key}`}>
                    {arr.map((item, i) => (
                        <div key={i} className="rounded-md border border-border p-3 bg-secondary/30 relative pr-10">
                            {field.subfields.map(sf => (
                                <div key={sf.key} className="mb-2 last:mb-0">
                                    {sf.label && <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{sf.label}</label>}
                                    {sf.type === "textarea" ? (
                                        <textarea rows={sf.rows || 2} value={item[sf.key] || ""} onChange={e => setAt(i, sf.key, e.target.value)} placeholder={sf.placeholder} className={`mt-0.5 ${inputCls}`} />
                                    ) : sf.type === "comma-array" ? (
                                        <input type="text" value={Array.isArray(item[sf.key]) ? item[sf.key].join(", ") : (item[sf.key] || "")} onChange={e => setAt(i, sf.key, e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder={sf.placeholder} className={`mt-0.5 ${inputCls}`} />
                                    ) : (
                                        <input type="text" value={item[sf.key] || ""} onChange={e => setAt(i, sf.key, e.target.value)} placeholder={sf.placeholder} className={`mt-0.5 ${inputCls}`} />
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => del(i)} className="absolute top-2 right-2 h-7 w-7 rounded-md text-muted-foreground hover:text-red-600 inline-flex items-center justify-center" data-testid={`entity-field-${field.key}-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                    ))}
                    <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-input px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary" data-testid={`entity-field-${field.key}-add`}>
                        <Plus className="h-3.5 w-3.5" /> Add item
                    </button>
                </div>
            </div>
        );
    }

    if (field.type === "section") {
        return (
            <div className="pt-5 pb-1">
                <div className="border-t-2 border-primary/20 mb-3" />
                <h3 className="text-xs font-bold uppercase tracking-widest text-primary">{field.label}</h3>
                {field.help && <p className="text-xs text-muted-foreground mt-0.5">{field.help}</p>}
            </div>
        );
    }

    if (field.type === "select") {
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <select value={value ?? ""} onChange={e => onChange(e.target.value)} className={`mt-1.5 ${inputCls}`} data-testid={`entity-field-${field.key}`}>
                    <option value="">{field.placeholder || "Select..."}</option>
                    {(field.options || []).map(opt => (
                        <option key={opt.value ?? opt} value={opt.value ?? opt}>{opt.label ?? opt}</option>
                    ))}
                </select>
                {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
            </div>
        );
    }

    if (field.type === "image" || field.type === "video") {
        return <UploadWidget label={field.label} value={value || ""} onChange={onChange} accept={field.type} testId={`entity-upload-${field.key}`} />;
    }

    if (field.type === "url") {
        return (
            <div>
                <label className={labelCls}>{field.label}</label>
                <input type="url" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={`mt-1.5 ${inputCls}`} data-testid={`entity-field-${field.key}`} />
                {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
            </div>
        );
    }

    // default: text
    return (
        <div>
            <label className={labelCls}>{field.label}</label>
            <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={`mt-1.5 ${inputCls}`} data-testid={`entity-field-${field.key}`} />
            {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
        </div>
    );
}

export default EntityForm;
